import React, { useState } from 'react';
import * as XLSX from 'xlsx';

const ImportClassModal = ({ isOpen, onClose, onImportSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [classDetails, setClassDetails] = useState({
    classNumber: '',
    section: '',
    semester: '',
    year: ''
  });

  const years = [2024, 2025, 2026, 2027];
  const semesters = ['Summer', 'Fall', 'Spring'];
  const classNumbers = [100, 101, 201, 224, 225, 227];
  const sections = ['BEW', 'RV', 'OR', 'GL', 'BEX'];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      if (!allowedTypes.includes(file.type) && !file.name.endsWith('.csv') && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.txt')) {
        setUploadError('Please select a CSV, Excel, or text file (.csv, .xlsx, .xls, .txt)');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setUploadError('');
    }
  };

  const parseExcel = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get the first worksheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            throw new Error('Excel file must contain at least a header row and one data row');
          }

          // Convert to the same format as CSV parser expects
          const headers = jsonData[0].map(h => (h || '').toString().trim().toLowerCase());
          const rows = jsonData.slice(1);

          resolve({ headers, rows });
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error.message}`));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read Excel file'));
      reader.readAsArrayBuffer(file);
    });
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must contain at least a header row and one data row');
    }

    // Auto-detect delimiter (comma or tab)
    const firstLine = lines[0];
    const commaCount = (firstLine.match(/,/g) || []).length;
    const tabCount = (firstLine.match(/\t/g) || []).length;
    const delimiter = tabCount > commaCount ? '\t' : ',';

    const headers = firstLine.split(delimiter).map(h => h.trim().toLowerCase());

    // Auto-detect column format
    const idIndex = headers.findIndex(h => {
      const header = h.toLowerCase().replace(/\s+/g, ''); // Remove spaces for comparison
      return header.includes('id') ||
             header.includes('orgdefinedid') ||
             header === 'id' ||
             header === 'studentid' ||
             header === 'userid' ||
             header === 'user_id' ||
             header === 'student_id' ||
             header === 'org_defined_id';
    });
    const nameIndex = headers.findIndex(h => h.includes('name') || h === 'name');
    const firstNameIndex = headers.findIndex(h => h.includes('first') && h.includes('name'));
    const lastNameIndex = headers.findIndex(h => h.includes('last') && h.includes('name'));
    const emailIndex = headers.findIndex(h => h.includes('email'));

    if (idIndex === -1) {
      const availableColumns = headers.join(', ');
      throw new Error(`CSV must contain an ID column. Found columns: ${availableColumns}. Looking for columns containing 'id', 'studentid', 'userid', etc.`);
    }

    const students = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(delimiter).map(v => v.trim());
      if (values.length === 0 || !values[idIndex]) continue; // Skip empty rows or rows without ID

      let student = { id: values[idIndex] };

      // Handle different column formats
      if (nameIndex !== -1 && values[nameIndex]) {
        // Format: ID, Name (where Name is "Last, First Middle")
        const fullName = values[nameIndex];
        const nameParts = fullName.split(',').map(part => part.trim());

        if (nameParts.length >= 2) {
          // "Last, First Middle" format
          student.lastName = nameParts[0];
          // Handle first name with middle name/initial
          const firstNameParts = nameParts[1].split(' ');
          student.firstName = firstNameParts[0];
          if (firstNameParts.length > 1) {
            // Include middle name/initial in firstName
            student.firstName = firstNameParts.slice(0, 2).join(' ');
          }
        } else {
          // Single name or different format - treat as first name
          student.firstName = fullName;
          student.lastName = '';
        }
      } else if (firstNameIndex !== -1 && lastNameIndex !== -1) {
        // Format: ID, First Name, Last Name, Email
        student.firstName = values[firstNameIndex] || '';
        student.lastName = values[lastNameIndex] || '';
      } else if (firstNameIndex !== -1) {
        // Only first name available
        student.firstName = values[firstNameIndex] || '';
        student.lastName = '';
      } else if (lastNameIndex !== -1) {
        // Only last name available
        student.firstName = '';
        student.lastName = values[lastNameIndex] || '';
      } else {
        // No name columns found
        student.firstName = '';
        student.lastName = '';
      }

      // Add email if available
      if (emailIndex !== -1 && values[emailIndex]) {
        student.email = values[emailIndex];
      } else {
        student.email = '';
      }

      students.push(student);
    }

    if (students.length === 0) {
      throw new Error('No valid student data found in the file');
    }

    return students;
  };

  const handleImport = async (event) => {
    event.preventDefault();

    if (!selectedFile) {
      setUploadError('Please select a file to import');
      return;
    }

    if (!classDetails.classNumber || !classDetails.section || !classDetails.semester || !classDetails.year) {
      setUploadError('Please fill in all class details');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      let students = [];

      // Check if it's an Excel file
      const isExcel = selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls') ||
                     selectedFile.type.includes('spreadsheet') || selectedFile.type.includes('excel');

      if (isExcel) {
        // Parse Excel file
        const { headers, rows } = await parseExcel(selectedFile);

        // Convert Excel data to student format using the same logic as CSV parser
        const idIndex = headers.findIndex(h => {
          const header = h.toLowerCase().replace(/\s+/g, '');
          return header.includes('id') ||
                 header.includes('orgdefinedid') ||
                 header === 'id' ||
                 header === 'studentid' ||
                 header === 'userid' ||
                 header === 'user_id' ||
                 header === 'student_id' ||
                 header === 'org_defined_id';
        });
        const nameIndex = headers.findIndex(h => h.includes('name') || h === 'name');
        const firstNameIndex = headers.findIndex(h => h.includes('first') && h.includes('name'));
        const lastNameIndex = headers.findIndex(h => h.includes('last') && h.includes('name'));
        const emailIndex = headers.findIndex(h => h.includes('email'));

        if (idIndex === -1) {
          const availableColumns = headers.join(', ');
          throw new Error(`Excel file must contain an ID column. Found columns: ${availableColumns}. Looking for columns containing 'id', 'studentid', 'userid', etc.`);
        }

        for (const row of rows) {
          if (!row || !row[idIndex]) continue; // Skip empty rows or rows without ID

          let student = { id: row[idIndex].toString() };

          // Handle different column formats (same logic as CSV parser)
          if (nameIndex !== -1 && row[nameIndex]) {
            // Format: ID, Name (where Name is "Last, First Middle")
            const fullName = row[nameIndex].toString();
            const nameParts = fullName.split(',').map(part => part.trim());

            if (nameParts.length >= 2) {
              // "Last, First Middle" format
              student.lastName = nameParts[0];
              // Handle first name with middle name/initial
              const firstNameParts = nameParts[1].split(' ');
              student.firstName = firstNameParts[0];
              if (firstNameParts.length > 1) {
                // Include middle name/initial in firstName
                student.firstName = firstNameParts.slice(0, 2).join(' ');
              }
            } else {
              // Single name or different format - treat as first name
              student.firstName = fullName;
              student.lastName = '';
            }
          } else if (firstNameIndex !== -1 && lastNameIndex !== -1) {
            // Format: ID, First Name, Last Name, Email
            student.firstName = row[firstNameIndex]?.toString() || '';
            student.lastName = row[lastNameIndex]?.toString() || '';
          } else if (firstNameIndex !== -1) {
            // Only first name available
            student.firstName = row[firstNameIndex]?.toString() || '';
            student.lastName = '';
          } else if (lastNameIndex !== -1) {
            // Only last name available
            student.firstName = '';
            student.lastName = row[lastNameIndex]?.toString() || '';
          } else {
            // No name columns found
            student.firstName = '';
            student.lastName = '';
          }

          // Add email if available
          if (emailIndex !== -1 && row[emailIndex]) {
            student.email = row[emailIndex].toString();
          } else {
            student.email = '';
          }

          students.push(student);
        }
      } else {
        // Parse text file (CSV/TXT)
        const fileText = await selectedFile.text();
        students = parseCSV(fileText);
      }

      if (students.length === 0) {
        throw new Error('No valid student data found in the file');
      }

      // Create the class name
      const className = `${classDetails.classNumber} ${classDetails.section} ${classDetails.semester} ${classDetails.year}`;

      // Call the success callback with the imported data
      if (onImportSuccess) {
        onImportSuccess({
          className,
          students,
          classDetails
        });
      }

      // Reset form and close modal
      setSelectedFile(null);
      setClassDetails({ classNumber: '', section: '', semester: '', year: '' });
      onClose();

      alert(`Successfully imported ${students.length} students for class: ${className}`);

    } catch (error) {
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClassDetailChange = (field, value) => {
    setClassDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-4">Import New Class</h2>

        {uploadError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {uploadError}
          </div>
        )}

        <form onSubmit={handleImport}>
          {/* File Upload Section */}
          <div className="mb-6">
            <label className="block text-sm font-bold mb-2">Upload Student File</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <input
                type="file"
                accept=".csv,.xlsx,.xls,.txt"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                disabled={isUploading}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer text-blue-600 hover:text-blue-800"
              >
                {selectedFile ? (
                  <div>
                    <div className="text-green-600 font-medium">✓ {selectedFile.name}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      Click to select a different file
                    </div>
                    <div className="text-xs text-gray-400 mt-2">
                      <strong>Supported formats:</strong><br/>
                      • ID, Name (Name as "Last, First Middle") ← Your format<br/>
                      • ID, First Name, Last Name<br/>
                      • ID, First Name, Last Name, Email<br/>
                      Email column is optional • Auto-detects comma or tab delimiters<br/>
                      • Excel files (.xlsx, .xls) supported
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-2xl mb-2">📁</div>
                    <div className="font-medium">Click to upload CSV or Excel file</div>
                    <div className="text-sm text-gray-500 mt-1">
                      Supports .csv, .xlsx, .xls, .txt formats (comma or tab delimited)
                    </div>
                    <div className="text-xs text-gray-400 mt-2">
                      <strong>Supported formats:</strong><br/>
                      • ID, Name (Name as "Last, First Middle") ← Your format<br/>
                      • ID, First Name, Last Name<br/>
                      • ID, First Name, Last Name, Email<br/>
                      Email column is optional • Auto-detects comma or tab delimiters<br/>
                      • Excel files (.xlsx, .xls) supported
                    </div>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Class Details Section */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold mb-2">Class Number</label>
              <select
                className="border rounded w-full py-2 px-3"
                value={classDetails.classNumber}
                onChange={(e) => handleClassDetailChange('classNumber', e.target.value)}
                disabled={isUploading}
              >
                <option value="">Select class number...</option>
                {classNumbers.map(num => <option key={num} value={num}>{num}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">Section</label>
              <select
                className="border rounded w-full py-2 px-3"
                value={classDetails.section}
                onChange={(e) => handleClassDetailChange('section', e.target.value)}
                disabled={isUploading}
              >
                <option value="">Select section...</option>
                {sections.map(sec => <option key={sec} value={sec}>{sec}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">Semester</label>
              <select
                className="border rounded w-full py-2 px-3"
                value={classDetails.semester}
                onChange={(e) => handleClassDetailChange('semester', e.target.value)}
                disabled={isUploading}
              >
                <option value="">Select semester...</option>
                {semesters.map(sem => <option key={sem} value={sem}>{sem}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">Year</label>
              <select
                className="border rounded w-full py-2 px-3"
                value={classDetails.year}
                onChange={(e) => handleClassDetailChange('year', e.target.value)}
                disabled={isUploading}
              >
                <option value="">Select year...</option>
                {years.map(year => <option key={year} value={year}>{year}</option>)}
              </select>
            </div>
          </div>

          <div className="flex justify-end mt-6 space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400"
              disabled={isUploading || !selectedFile}
            >
              {isUploading ? 'Importing...' : 'Import Class'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImportClassModal;
